import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  PLATFORM_ID,
  Inject,
  Renderer2,
  AfterViewInit,
  AfterViewChecked,
  OnDestroy,
} from '@angular/core';
import { DecimalPipe, isPlatformServer } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Directive({
  selector: '[app-animating-count]',
})
export class AnimatingCountDirective
  implements AfterViewInit, AfterViewChecked, OnDestroy
{
  private isAnimationTrigger = false;
  private langChangeSubs!: Subscription;

  @Input() count = 0;
  @Input() skips = 1;
  @Input() yOffset = 0;
  @Input() interval = 15;
  @Input() enableINRformat = false;
  @Input() registrationConvertToCrore = false;
  @Input() knowFormat = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private decimalPipe: DecimalPipe,
    private translate: TranslateService
  ) {}

  ngAfterViewInit() {
    const animationEssentials = () => {
      this.countDOMRef.textContent = '0';
      this.counterAnimationHandler();
    };

    this.langChangeSubs = this.translate.onLangChange.subscribe(() => {
      this.isAnimationTrigger = false;
      animationEssentials();
    });

    animationEssentials();
  }

  ngAfterViewChecked() {
    this.counterAnimationHandler();
  }

  @HostListener('document:scroll') onScrollEvent() {
    this.counterAnimationHandler();
  }

  private get countDOMRef() {
    return <HTMLElement>this.elementRef.nativeElement;
  }

  private counterAnimationHandler() {
    if (!this.isAnimationTrigger && this.isElementInViewport() && this.count) {
      this.isAnimationTrigger = true;
      this.triggerCountAnimation();
    }
  }

  private isElementInViewport() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const { innerHeight, innerWidth } = window;
    const { top, bottom, left, right } =
      this.countDOMRef.getBoundingClientRect();

    return (
      top >= 0 &&
      left >= 0 &&
      bottom <= innerHeight - this.yOffset &&
      right <= innerWidth
    );
  }

  private triggerCountAnimation() {
    this.isAnimationTrigger = true;
    let counter = 0;
    let { count, multiplier } = this.convertToWhole();

    const countSpaceElm: HTMLSpanElement = this.renderer.createElement('span');
    this.renderer.setProperty(
      countSpaceElm,
      'textContent',
      this.enableINRformat
        ? this.getINRFormat(count)
        : this.decimalPipe.transform(count, '1.0', 'en-US')
    );

    this.renderer.setStyle(countSpaceElm, 'visibility', 'hidden');
    this.renderer.appendChild(this.countDOMRef, countSpaceElm);

    this.renderer.setStyle(
      this.countDOMRef,
      'min-width',
      `${countSpaceElm.offsetWidth + 5}px`
    );
    this.renderer.setStyle(this.countDOMRef, 'display', 'inline-block');

    countSpaceElm.remove();

    const intervalRef = setInterval(() => {
      if (counter === count) {
        clearInterval(intervalRef);
        return;
      }

      counter += this.skips;
      counter = counter > count ? count : counter;
      const incrementCount = counter / multiplier;

      this.renderer.setProperty(
        this.countDOMRef,
        'textContent',
        this.enableINRformat
          ? this.getINRFormat(incrementCount)
          : this.decimalPipe.transform(incrementCount, '1.0', 'en-US')
      );
    }, this.interval);
  }

  private getINRFormat(count: number) {
    const format = this.identifyNumber(count);
    if (this.registrationConvertToCrore) {
      const num = count / 10000000;
      return `${num.toFixed(2)} ${format}`;
    } else {
      return `${+count.toString().slice(0, 5) / 100} ${format}`;
    }
  }

  identifyNumber(number: any) {
    if (number >= 10000000) {
      return this.translate.instant('stats.crores');
    } else if (number >= 100000) {
      return this.translate.instant('stats.lakhs');
    } else {
      return '';
    }
  }

  private convertToWhole() {
    const countStr = this.count.toString();
    const decimalIndex = countStr.indexOf('.');

    if (decimalIndex === -1) {
      return { count: this.count, multiplier: 1 };
    }

    const decimalDigitCount = countStr.slice(decimalIndex + 1).length;
    const multiplier = 10 ** decimalDigitCount;
    return { count: this.count * multiplier, multiplier };
  }

  ngOnDestroy() {
    this.langChangeSubs.unsubscribe();
  }
}
